import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import green from '@mui/material/colors/green';
import orange from '@mui/material/colors/orange';
import red from '@mui/material/colors/red';
import grey from '@mui/material/colors/grey';
import { withStyles } from 'tss-react/mui';
import { dayjs } from '@lba-dev/package.local-globals/dayjs';
import {
  IQG_STATS_CONFIG,
} from '@lba-dev/package.local-globals/iqg';

import api from '../../api';
import notifSystem from '../../notifSystem';
import { setDialog } from '../../actions/dialog';

const styles = {
  root: {
    textAlign: 'center',
    padding: 8,
  },
  scoreGreen: {
    color: green[600],
    fontWeight: 'bold',
    lineHeight: 1.1,
  },
  scoreOrange: {
    color: orange[600],
    fontWeight: 'bold',
    lineHeight: 1.1,
  },
  scoreRed: {
    color: red[600],
    fontWeight: 'bold',
    lineHeight: 1.1,
  },
  scoreGrey: {
    color: grey[500],
    fontWeight: 'bold',
    lineHeight: 1.1,
  },
  divider: {
    margin: '6px 0',
  },
  bold: {
    fontWeight: 'bold',
  },
  savColor: {
    fontWeight: 'bold',
    color: orange[600],
  },
  litigesColor: {
    fontWeight: 'bold',
    color: red[600],
  },
  historyButton: {
    marginTop: 6,
  },
};

const getScoreClass = (iqg, classes) => {
  if (iqg === null) {
    return classes.scoreGrey;
  }
  if (iqg >= 80) {
    return classes.scoreGreen;
  }
  if (iqg >= 50) {
    return classes.scoreOrange;
  }
  return classes.scoreRed;
};

const STYLE_KEYS = {
  interventions: 'bold',
  sav: 'savColor',
  litiges: 'litigesColor',
  mauvaisAvis: 'litigesColor',
};

const IQG_STATS = IQG_STATS_CONFIG.map((s) => ({
  ...s,
  styleKey: STYLE_KEYS[s.key],
}));

const baseFilter = (artisanId, from, to) => ({
  artisan: artisanId,
  'date.ajout': { $gte: from, $lte: to },
});

const openInterList = (stat, { artisanId, iqgData }, dispatch) => {
  const from = dayjs(iqgData.periodeDebut).toDate();
  const to = dayjs(iqgData.periodeFin).toDate();
  dispatch(setDialog({
    name: 'InterListDialog',
    open: true,
    hideClose: true,
    dialogProps: {
      title: stat.label,
      maxWidth: 'xl',
    },
    contentProps: {
      filter: { ...baseFilter(artisanId, from, to), ...stat.extraFilter },
      sort: { id: -1 },
      count: 100,
      displayName: 'decote',
    },
    actions: [
      {
        children: 'Fermer',
        color: 'secondary',
        onClick: (r, close) => close(),
      },
    ],
  }));
};

const openIQGHistorique = (artisanId, dispatch) =>
  dispatch(setDialog({
    name: 'IQGHistoryDialog',
    open: true,
    contentProps: { artisanId },
    dialogProps: {
      maxWidth: 'md',
    },
  }));

const IQGBlock = ({ artisanId, classes, dispatch }) => {
  const [loading, setLoading] = useState(false);
  const [iqgData, setIqgData] = useState(null);

  useEffect(() => {
    if (!artisanId) {
      return;
    }
    setLoading(true);
    api.stats
      .custom(`partenariat/artisan/${artisanId}/iqg`)
      .get()
      .then((res) => {
        setIqgData(res.body().data());
      })
      .catch(() => {
        notifSystem.error('Erreur', 'Impossible de charger l\'IQG');
      })
      .finally(() => setLoading(false));
  }, [artisanId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const iqg = iqgData ? iqgData.iqg : null;

  const periodeLabel = iqgData
    ? `De ${dayjs(iqgData.periodeDebut).format('MMM YYYY')} ` +
      `à ${dayjs(iqgData.periodeFin).format('MMM YYYY')}`
    : '';

  return (
    <Box className={classes.root}>
      <Typography variant="h6" color="textSecondary" gutterBottom>
        IQG (12 derniers mois)
      </Typography>
      <Typography
        variant="caption"
        color="textSecondary"
        display="block"
        gutterBottom
      >
        {periodeLabel}
      </Typography>
      <Tooltip
        disableInteractive
        title={
          iqgData
            ? `${iqgData.nbInterventions} interventions` +
              ` — ${iqgData.nbSAV} SAV` +
              ` — ${iqgData.nbLitiges} litiges qualité` +
              ` — ${iqgData.nbMauvaisAvis} mauvais avis`
            : ''
        }
      >
        <Typography
          variant="h4"
          className={getScoreClass(iqg, classes)}
        >
          {iqg !== null ? `${iqg}%` : 'N/A'}
        </Typography>
      </Tooltip>
      {iqgData?.updatedAt && (
        <Typography variant="caption" color="textSecondary">
          {
            `Mis à jour le ${dayjs(iqgData.updatedAt)
              .format('DD/MM/YYYY HH:mm')}`
          }
        </Typography>
      )}
      <Divider className={classes.divider} />
      {iqgData && (
        <Grid container justifyContent="center" spacing={1}>
          {IQG_STATS.map((stat) => (
            <Grid
              item
              key={stat.key}
              style={{ cursor: 'pointer' }}
              onClick={() =>
                openInterList(stat, { artisanId, iqgData }, dispatch)
              }
            >
              <Typography
                variant="caption"
                display="block"
                color="textSecondary"
              >
                {stat.label}
              </Typography>
              <Typography
                variant="body2"
                className={classes[stat.styleKey]}
              >
                {iqgData[stat.dataKey]}
              </Typography>
            </Grid>
          ))}
        </Grid>
      )}
      <Button
        size="small"
        variant="text"
        color="primary"
        className={classes.historyButton}
        onClick={() => openIQGHistorique(artisanId, dispatch)}
      >
        Voir historique
      </Button>
    </Box>
  );
};

const mapDispatchToProps = (dispatch) => ({ dispatch });

export default connect(
  null,
  mapDispatchToProps
)(withStyles(IQGBlock, styles));
